import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SetupStatusPill from '@/pages/apps/components/setup-status-pill';
import { app_provisioning_path } from '@/rails/routes';
import type { Provisioning, ProvisioningStatus } from '@/types/platform';
import { Form } from '@inertiajs/react';
import { Circle, CircleCheck } from 'lucide-react';

interface SetupCardProps {
  appId: string;
  // Null when setup has never been started for this app.
  provisioning: Provisioning | null;
}

const SUMMARIES: Record<ProvisioningStatus, string> = {
  pending: 'Setup is waiting to run. This page updates on its own.',
  running: 'Your app is being set up. This page updates on its own.',
  succeeded: 'Everything your app needs is in place.',
  failed: 'Setup did not finish.',
  halted: 'Setup stopped after two failures. CAP Rock Labs staff have to look at it.',
};

// Where setup stands, step by step, and a way to run it again when that can help.
const SetupCard = ({ appId, provisioning }: SetupCardProps) => {
  const status = provisioning?.status ?? 'pending';
  const steps = provisioning?.steps ?? [];
  // A stopped setup needs staff, so it gets no button.
  const canRetry = status === 'failed' || status === 'pending';

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>
          <h2>Setup</h2>
        </CardTitle>
        <CardAction>
          <SetupStatusPill status={status} />
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          {provisioning ? SUMMARIES[status] : 'Setup has not started for this app.'}
        </p>

        {steps.length > 0 ? (
          <ul className="space-y-2">
            {steps.map((step) => (
              <li key={step.key} className="flex items-center gap-2">
                {step.done ? (
                  <CircleCheck className="h-4 w-4 shrink-0 text-green-700" aria-hidden="true" />
                ) : (
                  <Circle className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1">{step.label}</span>
                <span className="text-muted-foreground text-xs">
                  {step.done ? 'Done' : 'Not done'}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {status === 'failed' && provisioning?.lastError ? (
          <p className="break-words text-red-700">{provisioning.lastError}</p>
        ) : null}

        {canRetry ? (
          <Form
            method="post"
            action={app_provisioning_path(appId)}
            options={{ preserveScroll: true }}
          >
            {({ processing }) => (
              <Button type="submit" variant="outline" disabled={processing}>
                Try again
              </Button>
            )}
          </Form>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default SetupCard;
